import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import inventoryApi from '../../../services/inventoryApi';
import '../inventory.css';
import './ProductForm.css';

/**
 * ProductRelatedTransfers Component
 * 
 * Shows stock transfers and cycle counts related to products:
 * - stock_transfer_items (Many-to-One to products)
 * - cycle_count_items (Many-to-One to products)
 */
const ProductRelatedTransfers = ({ organization = 'TestOrg' }) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeTab, setActiveTab] = useState('transfers');
  const [transfers, setTransfers] = useState([]);
  const [cycleCounts, setCycleCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadProducts();
  }, [organization]);

  useEffect(() => {
    if (selectedProduct) {
      loadData();
    }
  }, [selectedProduct, activeTab]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await inventoryApi.getProducts(organization);
      const productsData = Array.isArray(response) ? response : (response.data || []);
      setProducts(productsData);
      if (productsData.length > 0) {
        setSelectedProduct(productsData[0].id);
      }
    } catch (err) {
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    if (!selectedProduct) return;

    try {
      setLoading(true);
      setError(null);

      if (activeTab === 'transfers') {
        const response = await inventoryApi.getStockTransfers(organization);
        const allTransfers = Array.isArray(response) ? response : (response.data || []);
        
        const results = await Promise.all(allTransfers.map(async (transfer) => {
          const items = await inventoryApi.getStockTransferItems(transfer.id);
          const itemsData = Array.isArray(items) ? items : (items.data || []);
          const productItems = itemsData.filter(item => 
            (item.product_id || item.productId) === selectedProduct
          );
          return productItems.length > 0 ? { ...transfer, items: productItems } : null;
        }));
        setTransfers(results.filter(Boolean));
        
      } else if (activeTab === 'counts') {
        const response = await inventoryApi.getCycleCounts(organization);
        const allCounts = Array.isArray(response) ? response : (response.data || []);
        
        const results = await Promise.all(allCounts.map(async (count) => {
          const items = await inventoryApi.getCycleCountItems(count.id);
          const itemsData = Array.isArray(items) ? items : (items.data || []);
          const productItems = itemsData.filter(item => 
            (item.product_id || item.productId) === selectedProduct
          );
          return productItems.length > 0 ? { ...count, items: productItems } : null;
        }));
        setCycleCounts(results.filter(Boolean));
      }
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'draft': 'status-draft',
      'pending': 'status-pending',
      'in_transit': 'status-in-transit',
      'completed': 'status-completed',
      'cancelled': 'status-cancelled',
      'approved': 'status-approved',
      'rejected': 'status-rejected'
    };
    return statusMap[status?.toLowerCase()] || 'status-other';
  };

  const renderTransfers = () => (
    <div>
      {loading ? (
        <div className="pm-skeleton" aria-busy="true">
          {t('inventory.loading_stock_transfers')}
        </div>
      ) : transfers.length === 0 ? (
        <div className="pm-empty-state">
          <div className="pm-empty-state-icon" aria-hidden="true">🔄</div>
          <h3>{t('inventory.no_stock_transfers')}</h3>
        </div>
      ) : (
        transfers.map(transfer => (
          <div key={transfer.id} className="pm-transfer-card">
            <div className="pm-card-header">
              <div className="pm-card-info">
                <h4>{t('inventory.transfer_prefix')}{transfer.transfer_number || transfer.transferNumber || transfer.id}</h4>
                <span className={`pm-status-badge ${getStatusClass(transfer.status)}`}>
                  {(transfer.status || 'draft').toUpperCase()}
                </span>
              </div>
              <div className="pm-card-meta">
                <span>{t('inventory.date_label')} {formatDate(transfer.transfer_date || transfer.transferDate)}</span>
                <span>{t('inventory.from_label')} {transfer.from_warehouse_name || transfer.fromWarehouseName || t('inventory.na_fallback')}</span>
                <span>{t('inventory.to_label')} {transfer.to_warehouse_name || transfer.toWarehouseName || t('inventory.na_fallback')}</span>
              </div>
            </div>
            <div className="pm-card-content">
              <table className="pm-data-table">
                <thead>
                  <tr>
                    <th>{t('inventory.quantity_header')}</th>
                    <th>{t('inventory.from_location_header')}</th>
                    <th>{t('inventory.to_location_header')}</th>
                    <th>{t('inventory.lot_number_header')}</th>
                    <th>{t('inventory.serial_number_header')}</th>
                    <th>{t('inventory.status_header')}</th>
                  </tr>
                </thead>
                <tbody>
                  {transfer.items.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.quantity}</td>
                      <td>{item.from_location || item.fromLocation || t('inventory.na_fallback')}</td>
                      <td>{item.to_location || item.toLocation || t('inventory.na_fallback')}</td>
                      <td>{item.lot_number || item.lotNumber || '-'}</td>
                      <td>{item.serial_number || item.serialNumber || '-'}</td>
                      <td>
                        <span className={`pm-status-badge ${getStatusClass(item.status)}`}>
                          {(item.status || 'pending').toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {transfer.notes && (
              <div className="pm-card-notes">
                <strong>{t('inventory.notes_label')}</strong> {transfer.notes}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  const renderCycleCounts = () => (
    <div>
      {loading ? (
        <div className="pm-skeleton" aria-busy="true">
          {t('inventory.loading_cycle_counts')}
        </div>
      ) : cycleCounts.length === 0 ? (
        <div className="pm-empty-state">
          <div className="pm-empty-state-icon" aria-hidden="true">🔢</div>
          <h3>{t('inventory.no_cycle_counts')}</h3>
        </div>
      ) : (
        cycleCounts.map(count => (
          <div key={count.id} className="pm-transfer-card">
            <div className="pm-card-header">
              <div className="pm-card-info">
                <h4>{t('inventory.count_prefix')}{count.count_number || count.countNumber || count.id}</h4>
                <span className={`pm-status-badge ${getStatusClass(count.status)}`}>
                  {(count.status || 'draft').toUpperCase()}
                </span>
              </div>
              <div className="pm-card-meta">
                <span>{t('inventory.date_label')} {formatDate(count.count_date || count.countDate)}</span>
                <span>{t('inventory.warehouse_label')} {count.warehouse_name || count.warehouseName || t('inventory.na_fallback')}</span>
                <span>{t('inventory.counted_by_label')} {count.counted_by_name || count.countedByName || t('inventory.na_fallback')}</span>
              </div>
            </div>
            <div className="pm-card-content">
              <table className="pm-data-table">
                <thead>
                  <tr>
                    <th>{t('inventory.location_header')}</th>
                    <th>{t('inventory.expected_qty_header')}</th>
                    <th>{t('inventory.counted_qty_header')}</th>
                    <th>{t('inventory.variance_header')}</th>
                    <th>{t('inventory.lot_number_header')}</th>
                    <th>{t('inventory.serial_number_header')}</th>
                    <th>{t('inventory.notes_header')}</th>
                  </tr>
                </thead>
                <tbody>
                  {count.items.map((item, idx) => {
                    const expected = parseFloat(item.expected_quantity || item.expectedQuantity || 0);
                    const counted = parseFloat(item.counted_quantity || item.countedQuantity || 0);
                    const variance = counted - expected;
                    const hasVariance = Math.abs(variance) > 0.01;
                    
                    return (
                      <tr key={idx} className={hasVariance ? 'row-variance' : ''}>
                        <td>{item.location || t('inventory.na_fallback')}</td>
                        <td>{expected.toFixed(2)}</td>
                        <td>{counted.toFixed(2)}</td>
                        <td className={variance > 0 ? 'pm-variance-positive' : variance < 0 ? 'pm-variance-negative' : ''}>
                          {variance > 0 ? '+' : ''}{variance.toFixed(2)}
                        </td>
                        <td>{item.lot_number || item.lotNumber || '-'}</td>
                        <td>{item.serial_number || item.serialNumber || '-'}</td>
                        <td>{item.notes || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {count.notes && (
              <div className="pm-card-notes">
                <strong>{t('inventory.notes_label')}</strong> {count.notes}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="pm-page-card">
      <div className="pm-header">
        <h2 className="pm-header-title">{t('inventory.product_related_transfers_title')}</h2>
      </div>

      {error && (
        <div className="pm-error-banner">
          <span aria-hidden="true">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      <div className="pm-product-selector">
        <label>{t('inventory.select_product_label')}</label>
        <select 
          value={selectedProduct || ''} 
          onChange={(e) => setSelectedProduct(Number(e.target.value))}
        >
          <option value="">{t('inventory.select_product_option')}</option>
          {products.map(product => (
            <option key={product.id} value={product.id}>
              {product.name} ({product.sku || product.SKU || t('inventory.na_fallback')})
            </option>
          ))}
        </select>
      </div>

      <div className="pm-tabs">
        <button
          className={`pm-tab ${activeTab === 'transfers' ? 'active' : ''}`}
          onClick={() => setActiveTab('transfers')}
        >
          {t('inventory.stock_transfers_tab')}
        </button>
        <button
          className={`pm-tab ${activeTab === 'counts' ? 'active' : ''}`}
          onClick={() => setActiveTab('counts')}
        >
          {t('inventory.cycle_counts_tab')}
        </button>
      </div>

      <div>
        {activeTab === 'transfers' && renderTransfers()}
        {activeTab === 'counts' && renderCycleCounts()}
      </div>
    </div>
  );
};

export default ProductRelatedTransfers;
